import { useState } from "react";

/** Each field's problem, or undefined when it's fine. */
export type FieldErrors = Record<string, string | undefined>;

/**
 * Field errors that wait for the first try. A form works out each field's problem on every render;
 * nothing shows until the person presses its submit button, and from then on each field says what
 * it needs, and stops saying it once it's fixed. The button stays enabled, so pressing it always
 * explains what's missing instead of doing nothing.
 *
 *   const check = useFormCheck();
 *   const errors = { plate: plate.trim() ? undefined : "Enter the plate." };
 *   <Input error={check.show(errors.plate)} … />
 *   <Button onClick={() => check.submit(errors, () => save.mutate())}>Add truck</Button>
 */
export function useFormCheck() {
  const [tried, setTried] = useState(false);
  return {
    /** The person has pressed submit at least once. */
    tried,
    /** A field's error, once the person has tried to submit. */
    show: (error: string | undefined) => (tried ? error : undefined),
    /** Shows every error; runs `go` only when there are none. */
    submit(errors: FieldErrors, go: () => void) {
      setTried(true);
      if (Object.values(errors).every((e) => !e)) go();
    },
    /** For a reopened form: back to showing nothing. */
    reset: () => setTried(false),
  };
}
